import { Chip } from '@/components/ui'
import type { PriceReviewLine } from '../api/reviewTypes'
import { summarize } from '../utils/review-lines'

function Swatch({ className, label, count }: { className: string; label: string; count?: number }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span aria-hidden="true" className={`inline-block h-5 w-8 rounded-xs border ${className}`} />
      <span className="text-primary">{label}</span>
      {count !== undefined ? <span className="text-tertiary tabular-nums">({count})</span> : null}
    </span>
  )
}

export interface ReviewLegendProps {
  lines: readonly PriceReviewLine[]
}

/**
 * What the colours on the grid mean, always visible above it. The swatches use
 * the same background tokens as the rows, so they can never drift from what is
 * on screen, and the counts say how many lines each colour applies to.
 */
export function ReviewLegend({ lines }: ReviewLegendProps) {
  const noVolume = lines.filter((l) => l.inactive && !l.flagged).length
  const flagged = lines.filter((l) => l.flagged).length
  const changed = lines.filter((l) => l.price_changed).length
  const s = summarize(lines)
  return (
    <div
      role="note"
      aria-label="Colour key"
      className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-sm border border-border-subtle bg-surface-sunk px-4 py-2.5 text-md"
    >
      <span className="text-sm font-semibold uppercase tracking-wide text-tertiary">Key</span>
      <Swatch className="border-danger bg-danger-bg" label="No volume in the last 12 months" count={noVolume} />
      <Swatch className="border-warning bg-warning-bg" label="Flagged for discussion" count={flagged} />
      <Swatch className="border-success-border bg-success-bg" label="New price changed" count={changed} />
      <span className="inline-flex items-center gap-2">
        <Chip variant="warning">Units differ</Chip>
        <span className="text-secondary">cost and price in different units: no margin shown</span>
      </span>
      <span className="ml-auto text-secondary">
        {s.changes} price {s.changes === 1 ? 'change' : 'changes'} · {s.discontinues} removed · {s.kept} unchanged
        {s.missingPrice > 0 ? ` · ${s.missingPrice} marked Change with no price yet` : ''}
      </span>
    </div>
  )
}
